import {
  CalendarClock,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Brand } from "../components/brand";
import { Button, Field, Notice } from "../components/ui";
import {
  acceptTeamInvitation,
  acceptTeamPasswordReset,
  describeTeamAccessCodeError,
  describeApiError,
  requestPasswordRecovery,
} from "../lib/api";
import { useAuth } from "../lib/auth-context";
import type { PublicTeamAccessKind } from "../lib/team-access";

function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="password-control">
      <input
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="icon-button"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? "Скрыть пароль" : "Показать пароль"}
      >
        {visible ? <EyeOff aria-hidden="true" size={19} /> : <Eye aria-hidden="true" size={19} />}
      </button>
    </div>
  );
}

function AuthErrorNotice({ message }: { message: string | null }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (message) containerRef.current?.focus();
  }, [message]);

  if (!message) return null;

  return (
    <div ref={containerRef} className="auth-form__error" tabIndex={-1}>
      <Notice tone="danger" title={message} />
    </div>
  );
}

function AuthLayout({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <main className="auth-layout">
      <section className="auth-intro">
        <Brand />
        <div>
          <h1>Каждый лид получает правильный следующий шаг.</h1>
          <p>
            Яна ведёт новые обращения до квалификации и записи на созвон, а Вы проверяете её ответы и действия.
          </p>
        </div>
        <ul>
          <li><ShieldCheck aria-hidden="true" size={18} />Отправка клиентам выключена по умолчанию</li>
          <li><LockKeyhole aria-hidden="true" size={18} />Критичные изменения подтверждаются на сервере</li>
          <li><KeyRound aria-hidden="true" size={18} />Все решения сохраняются в журнале</li>
        </ul>
      </section>
      <section className="auth-panel">
        <div className="auth-panel__heading">
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        {children}
      </section>
    </main>
  );
}

/** The last choice of «Запомнить меня» on this device; on by default (20.10.2026: staff signed in again every morning). */
const REMEMBER_KEY = "vekha.login.remember";

function rememberedChoice(): boolean {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) !== "0";
  } catch {
    return true;
  }
}

export function LoginPage() {
  const { signIn, error, pending } = useAuth();
  const emailId = useId();
  const passwordId = useId();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(rememberedChoice);
  const [localError, setLocalError] = useState<string | null>(null);

  function changeRemember(next: boolean) {
    setRemember(next);
    try {
      window.localStorage.setItem(REMEMBER_KEY, next ? "1" : "0");
    } catch {
      // A private window without storage asks again next time.
    }
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim() || !password) {
      setLocalError("Введите рабочую почту и пароль.");
      return;
    }
    setLocalError(null);
    await signIn({ email: email.trim(), password, remember });
  }

  return (
    <AuthLayout title="Вход в Веху" description="Используйте учётную запись Академии гипнотерапии.">
      <form className="auth-form" onSubmit={submit} noValidate>
        <AuthErrorNotice message={localError ?? error} />
        <Field label="Рабочая почта" htmlFor={emailId}>
          <input
            id={emailId}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            inputMode="email"
          />
        </Field>
        <Field label="Пароль" htmlFor={passwordId}>
          <PasswordInput
            id={passwordId}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
        </Field>
        <label className="auth-remember">
          <input type="checkbox" checked={remember} onChange={(event) => changeRemember(event.target.checked)} />
          <span className="auth-remember__box" aria-hidden="true"><Check size={15} strokeWidth={3} /></span>
          <span className="auth-remember__text">
            Запомнить меня на 30 дней
            <small>На чужом устройстве снимите галочку</small>
          </span>
        </label>
        <Button type="submit" disabled={pending}>
          {pending ? "Проверяем…" : "Войти"}
        </Button>
        <p className="auth-form__help">
          Доступ создаёт владелец. Яна не отправляет пароль по почте и не просит его в чате.
        </p>
        <div className="auth-form__links" aria-label="Доступ по одноразовому коду">
          <Link to="/accept-invitation">Есть код приглашения</Link>
          <Link to="/forgot-password">Забыли пароль?</Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const emailId = useId();
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email.trim())) {
      setError("Введите рабочую почту полностью.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      await requestPasswordRecovery(email.trim());
      setSent(true);
    } catch (caught) {
      setError(describeApiError(caught));
    } finally { setPending(false); }
  }
  return <AuthLayout title="Восстановить пароль" description="Введите почту, с которой Вы входите в Веху. Пришлём ссылку для нового пароля.">
    {sent ? <div className="access-code-success">
      <Notice tone="safe" title="Проверьте почту">
        Если для {email.trim()} есть действующий доступ, письмо придёт в ближайшие минуты. Ссылка действует 30 минут.
        Проверьте также папку «Спам». Если письма нет, повторите запрос через пару минут.
      </Notice>
      <Button type="button" onClick={() => setSent(false)}>Вернуться к адресу почты</Button>
      <Link className="auth-form__back" to="/">Вернуться ко входу</Link>
    </div> : <form className="auth-form" onSubmit={submit} noValidate>
      <AuthErrorNotice message={error} />
      <Field label="Рабочая почта" htmlFor={emailId}>
        <input id={emailId} type="email" inputMode="email" autoComplete="email" value={email}
          onChange={event => setEmail(event.target.value)} disabled={pending} />
      </Field>
      <Button type="submit" disabled={pending}>{pending ? "Отправляем запрос…" : "Получить ссылку"}</Button>
      <Link className="auth-form__back" to="/">Вернуться ко входу</Link>
    </form>}
  </AuthLayout>;
}

export function TeamAccessCodePage({ kind }: { kind: PublicTeamAccessKind }) {
  const codeId = useId();
  const passwordId = useId();
  const confirmationId = useId();
  const [code, setCode] = useState(()=>{
    if(typeof window === "undefined")return "";
    const fragment=new URLSearchParams(window.location.hash.slice(1));
    const value=fragment.get("code") ?? "";
    return /^[A-Za-z0-9_-]{43}$/u.test(value) ? value : "";
  });
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committed, setCommitted] = useState(false);
  const invitation = kind === "invitation";
  useEffect(()=>{
    if(new URLSearchParams(window.location.hash.slice(1)).has("code"))window.history.replaceState(null,"",window.location.pathname+window.location.search);
  },[]);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^[A-Za-z0-9_-]{43}$/u.test(code.trim())) {
      setError("Введите полный одноразовый код из 43 символов.");
      return;
    }
    if (password.length < 12) {
      setError("Новый пароль должен содержать не менее 12 символов.");
      return;
    }
    if (password !== confirmation) {
      setError("Пароли не совпадают.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const input = { code: code.trim(), newPassword: password };
      if (invitation) await acceptTeamInvitation(input);
      else await acceptTeamPasswordReset(input);
      setCode("");
      setPassword("");
      setConfirmation("");
      setCommitted(true);
    } catch (caught) {
      setError(invitation ? describeTeamAccessCodeError(caught) : describeTeamAccessCodeError(caught).replace("Одноразовый код недействителен, истёк, отозван или уже использован", "Ссылка недействительна, истекла или уже использована").replace("Проверьте полный одноразовый код и новый пароль", "Проверьте новый пароль или запросите новую ссылку"));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title={invitation ? "Получить доступ" : "Задать новый пароль"}
      description={invitation
        ? "Задайте пароль для входа. Код из ссылки приглашения уже подставлен; его также можно ввести вручную."
        : "Задайте новый пароль для входа в Веху."}
    >
      {committed ? (
        <div className="access-code-success">
          <Notice tone="safe" title={invitation ? "Доступ настроен" : "Пароль заменён"}>
            Войдите с рабочей почтой и новым паролем.
          </Notice>
          <a className="button button--primary" href="/">Перейти ко входу</a>
        </div>
      ) : !invitation && !code ? (
        <div className="access-code-success">
          <Notice tone="info" title="Нужна ссылка из письма">Откройте письмо от Вехи или запросите новую ссылку для восстановления.</Notice>
          <Link className="button button--primary" to="/forgot-password">Получить ссылку</Link>
          <Link className="auth-form__back" to="/">Вернуться ко входу</Link>
        </div>
      ) : (
        <form className="auth-form" onSubmit={submit} noValidate>
          <AuthErrorNotice message={error} />
          {!invitation && error && <Link to="/forgot-password">Запросить новую ссылку</Link>}
          {invitation && <Notice tone="info" title="Личный доступ">
            Не пересылайте приглашение посторонним. Яна не просит пароль или код в чате.
          </Notice>}
          {invitation && <Field label="Одноразовый код" htmlFor={codeId} hint="43 символа; пробелы в начале и конце будут удалены.">
            <input
              id={codeId}
              type="password"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>}
          <Field label="Новый пароль" htmlFor={passwordId} hint="Не менее 12 символов.">
            <PasswordInput id={passwordId} value={password} onChange={setPassword} autoComplete="new-password" />
          </Field>
          <Field label="Повторите новый пароль" htmlFor={confirmationId}>
            <PasswordInput id={confirmationId} value={confirmation} onChange={setConfirmation} autoComplete="new-password" />
          </Field>
          <Button type="submit" disabled={pending}>
            {pending
              ? "Подтверждаем на сервере…"
              : invitation
                ? "Задать пароль и получить доступ"
                : "Сохранить новый пароль"}
          </Button>
          <Link className="auth-form__back" to="/">Вернуться ко входу</Link>
        </form>
      )}
    </AuthLayout>
  );
}

export function SetupPage() {
  const { finishSetup, error, pending, setupExpiresAt } = useAuth();
  const [setupToken, setSetupToken] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [installationName, setInstallationName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (setupToken.trim().length < 32) {
      setLocalError("Введите полный одноразовый ключ установки.");
      return;
    }
    if (!displayName.trim() || !email.trim() || !organizationName.trim() || !installationName.trim()) {
      setLocalError("Заполните все поля организации и владельца.");
      return;
    }
    if (password.length < 12) {
      setLocalError("Пароль должен содержать не менее 12 символов.");
      return;
    }
    if (password !== confirmation) {
      setLocalError("Пароли не совпадают.");
      return;
    }
    setLocalError(null);
    await finishSetup({
      setupToken: setupToken.trim(),
      displayName: displayName.trim(),
      email: email.trim(),
      organizationName: organizationName.trim(),
      installationName: installationName.trim(),
      password,
    });
  }

  return (
    <AuthLayout
      title="Первичная настройка"
      description="Создайте владельца и организацию. Одноразовый ключ вводится только здесь и не попадает в адрес страницы."
    >
      <form className="auth-form auth-form--setup" onSubmit={submit} noValidate>
        {setupExpiresAt ? (
          <Notice tone="info" title="Ключ установки ограничен по времени">
            Он действует до {new Date(setupExpiresAt).toLocaleString("ru-RU", {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}.
          </Notice>
        ) : null}
        <AuthErrorNotice message={localError ?? error} />
        <Field label="Одноразовый ключ установки" htmlFor="setup-token" hint="Получен при безопасной установке сервера.">
          <input
            id="setup-token"
            type="password"
            value={setupToken}
            onChange={(event) => setSetupToken(event.target.value)}
            autoComplete="off"
          />
        </Field>
        <div className="auth-form__columns">
          <Field label="Ваше имя" htmlFor="setup-name">
            <input id="setup-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" />
          </Field>
          <Field label="Рабочая почта" htmlFor="setup-email">
            <input id="setup-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" inputMode="email" />
          </Field>
          <Field label="Организация" htmlFor="setup-organization">
            <input id="setup-organization" value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} autoComplete="organization" />
          </Field>
          <Field label="Название рабочего пространства" htmlFor="setup-installation">
            <input id="setup-installation" value={installationName} onChange={(event) => setInstallationName(event.target.value)} autoComplete="off" />
          </Field>
          <Field label="Пароль" htmlFor="setup-password" hint="Не менее 12 символов.">
            <PasswordInput id="setup-password" value={password} onChange={setPassword} autoComplete="new-password" />
          </Field>
          <Field label="Повторите пароль" htmlFor="setup-password-confirmation">
            <PasswordInput id="setup-password-confirmation" value={confirmation} onChange={setConfirmation} autoComplete="new-password" />
          </Field>
        </div>
        <Button type="submit" disabled={pending}>
          {pending ? "Создаём защищённую учётную запись…" : "Завершить настройку"}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function SetupExpiredPage() {
  const { setupExpiresAt } = useAuth();
  const expiredLabel = setupExpiresAt
    ? new Date(setupExpiresAt).toLocaleString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <AuthLayout
      title="Срок ключа установки истёк"
      description="Система Яны не принимает просроченный одноразовый ключ и не показывает форму создания владельца."
    >
      <div className="setup-expired">
        <Notice tone="danger" title="Первичная настройка заблокирована">
          {expiredLabel ? `Ключ перестал действовать ${expiredLabel}.` : "Одноразовый ключ больше не действует."}
        </Notice>
        <div className="setup-expired__steps">
          <CalendarClock aria-hidden="true" size={23} />
          <div>
            <h3>Что нужно сделать</h3>
            <ol>
              <li>Администратор сервера выпускает новый одноразовый ключ установки.</li>
              <li>Обновите эту страницу после выпуска ключа.</li>
              <li>Введите новый ключ только в поле формы — не добавляйте его в адрес страницы.</li>
            </ol>
          </div>
        </div>
        <Button icon={RefreshCw} onClick={() => window.location.reload()}>
          Проверить новый ключ
        </Button>
      </div>
    </AuthLayout>
  );
}
